import { useRef } from 'react';
import { PROCESS } from '../content';
import { Accordion, useAccordionCycle } from './Capabilities';
import Reveal from './Reveal';
import './Process.css';

// A vertical-profile chart (as in an EFB) where each step of the engagement
// is a waypoint on the climb. `at` is the waypoint's position along the path
// in percent of its length; `pitch` is the angle of the aircraft at that fix.
const FIXES = [
  { x: 92, y: 330, at: 8.5, pitch: -6 },
  { x: 250, y: 252, at: 36.5, pitch: -27 },
  { x: 410, y: 150, at: 66, pitch: -24 },
  { x: 566, y: 92, at: 92, pitch: -4 },
];
const PROFILE = 'M36 336 L92 330 C150 318 200 280 250 252 S350 178 410 150 S520 98 566 92 L620 92';
const LEVELS = [
  ['FL400', 76],
  ['FL300', 140],
  ['FL200', 204],
  ['FL100', 268],
  ['GND', 336],
];

function ClimbProfile({ active }) {
  const fix = FIXES[active];
  return (
    <svg
      className="profile"
      viewBox="0 0 640 400"
      role="img"
      aria-label={`Step ${active + 1} of ${PROCESS.length}: ${PROCESS[active].title}`}
    >
      {LEVELS.map(([label, y]) => (
        <g key={label}>
          <line className="profile__grid" x1="64" x2="640" y1={y} y2={y} />
          <text className="profile__lvl" x="0" y={y + 4}>
            {label}
          </text>
        </g>
      ))}
      <path
        className="profile__terrain"
        d="M0 400V350c50-6 90 2 140-4s80-14 130-6 70 10 120 2 90-12 140-4 60 6 110 2V400Z"
      />
      <path className="profile__route" d={PROFILE} pathLength="100" />
      <path className="profile__flown" d={PROFILE} pathLength="100" style={{ strokeDashoffset: 100 - fix.at }} />
      {FIXES.map((f, i) => (
        <g
          key={PROCESS[i].fix}
          className={`profile__fix${i < active ? ' is-past' : ''}${i === active ? ' is-active' : ''}`}
          transform={`translate(${f.x} ${f.y})`}
        >
          <circle className="profile__halo" r="16" />
          <circle className="profile__dot" r="5.5" />
          <text className="profile__name" y="-26" textAnchor="middle">
            {PROCESS[i].fix}
          </text>
        </g>
      ))}
      <g className="profile__plane" style={{ transform: `translate(${fix.x}px, ${fix.y}px) rotate(${fix.pitch}deg)` }}>
        <path d="M17 0 6-3.5V-12L2.5-13V-3.5L-10-1.5-13.5-7.5h-2.2l1.2 7.5-1.2 7.5h2.2l3.5-6L2.5 3.5V13L6 12V3.5L17 0Z" />
      </g>
    </svg>
  );
}

export default function Process() {
  const ref = useRef(null);
  const { active, select, cycle, running, hoverProps } = useAccordionCycle(ref, PROCESS.length);

  return (
    <section id="approach" ref={ref} className="section tone-light process" data-nav="light">
      <div className="container process__grid">
        <div className="process__copy">
          <Reveal className="section-head">
            <span className="eyebrow">Approach</span>
            <h2 className="h2">
              From first call to <em>wheels-up</em>.
            </h2>
          </Reveal>
          <Reveal delay={120} {...hoverProps}>
            <Accordion
              items={PROCESS}
              active={active}
              onSelect={select}
              running={running}
              cycle={cycle}
              idPrefix="step"
              renderLead={(item) => <span className="acc__fix mono">{item.fix}</span>}
            />
          </Reveal>
        </div>
        <Reveal className="process__visual" delay={160}>
          <div className="process__chart-head mono">
            <span>Vertical profile</span>
            <span>
              {PROCESS[0].fix} → {PROCESS[PROCESS.length - 1].fix}
            </span>
          </div>
          <ClimbProfile active={active} />
          <div className="process__step">
            <span className="mono">
              {String(active + 1).padStart(2, '0')} / {String(PROCESS.length).padStart(2, '0')}
            </span>
            <b>{PROCESS[active].title}</b>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
